import React from 'react';
import { Gem } from 'lucide-react';
import { BookCallButton } from './BookCallButton';
import { experiencePillars, treasuryFeatures } from '../../data/foundingProgram';
const TREASURY_IMAGE = 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png';
export function FoundingStory() {
  return <>
      {/* Why now */}
      <section className="border-b border-[#D4AF37]/12 bg-[#08070A]" data-id="element-1429" data-mp="1c53i1nk9vy2m">
        <div className="mx-auto grid w-full max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20 lg:py-24" data-id="element-1430" data-mp="oe7ke9t4y8ng">
          <div data-id="element-1431" data-mp="kdxbopdyl44w">
            <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-[#D4AF37]" data-id="element-1432" data-mp="ybm0u9f26xfr">
              Why now
            </p>
            <h2 className="mt-5 font-serif text-3xl leading-tight text-[#FDFBF7] sm:text-5xl" data-id="element-1433" data-mp="tbhhmbmrnolu">
              Our history has never been told at this scale.
            </h2>
          </div>
          <div className="space-y-6 text-lg font-light leading-relaxed text-[#FDFBF7]/70" data-id="element-1434" data-mp="24s7kbkrzdyap">
            <p data-id="element-1435" data-mp="26lm5qycmqgw0">
              For two centuries the Sikh Kingdoms have been narrated by
              everybody except us — in colonial archives, in footnotes, in
              display cases behind glass in other people’s museums.
            </p>
            <p data-id="element-1436" data-mp="2gk1yt6v7w085">
              <span className="text-[#FDFBF7]" data-id="element-1437" data-mp="2gh7hho7i2viy">
                Illuminarium Toronto changes the terms.
              </span>{' '}
              It is one of the most advanced immersive venues in North America,
              and for the first time an entire season of it belongs to the
              Lahore Darbar. Think Universal Studios meets the Wizarding World —
              rooted in Punjab, produced with scholarship, and built for a
              diaspora that has never seen itself rendered this way.
            </p>
            <p data-id="element-1438" data-mp="154d9w9yekr4p">
              What is missing is not ambition or craft. It is a group of Punjabi
              professionals willing to stand behind it early, publicly, and by
              name.
            </p>
          </div>
        </div>
      </section>

      {/* The experience */}
      <section className="border-b border-[#D4AF37]/12 bg-[#0B0A0E]" data-id="element-1439" data-mp="x3ff6f5fp60s">
        <div className="mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 lg:py-24" data-id="element-1440" data-mp="x35oepckeuu1">
          <div className="max-w-2xl" data-id="element-1441" data-mp="1br0ia92b758p">
            <p className="text-[10px] font-medium uppercase tracking-[0.32em] text-[#D4AF37]" data-id="element-1442" data-mp="1bu685qirib5h">
              The experience
            </p>
            <h2 className="mt-5 font-serif text-3xl leading-tight text-[#FDFBF7] sm:text-5xl" data-id="element-1443" data-mp="j2ux5l7cfe8c">
              Sixty minutes inside the court, and a decade of work around it.
            </h2>
          </div>

          <div className="mt-14 grid gap-px border-t border-[#D4AF37]/15 sm:grid-cols-2" data-id="element-1444" data-mp="t2vifcorw4f6">
            {experiencePillars.map(pillar => {
            const Icon = pillar.icon;
            return <article key={pillar.title} className="flex flex-col border-b border-[#D4AF37]/12 py-9 pr-8 sm:[&:nth-child(odd)]:border-r sm:[&:nth-child(odd)]:pr-12 sm:[&:nth-child(even)]:pl-12" data-id="element-1445" data-mp="24fxw9zzhmfvn">
                  <Icon className="h-6 w-6 text-[#D4AF37]" aria-hidden="true" data-id="element-1446" data-mp="2c0ic60stq0fi" />
                  <h3 className="mt-7 font-serif text-2xl text-[#FDFBF7]" data-id="element-1447" data-mp="1y1sfgm9x22ro">
                    {pillar.title}
                  </h3>
                  <p className="mt-3 text-base font-light leading-relaxed text-[#FDFBF7]/60" data-id="element-1448" data-mp="222m1tb7pn9ig">
                    {pillar.body}
                  </p>
                </article>;
          })}
          </div>
        </div>
      </section>

      {/* Digital Treasury */}
      <section className="border-b border-[#D4AF37]/12 bg-[#08070A]" data-id="element-1449" data-mp="2gytl89bao6wo">
        <div className="mx-auto grid w-full max-w-7xl gap-14 px-5 py-20 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-20 lg:py-24" data-id="element-1450" data-mp="2l0ok775r687a">
          <div data-id="element-1451" data-mp="38phllj55989a">
            <p className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.32em] text-[#D4AF37]" data-id="element-1452" data-mp="38ooeui66qir5">
              <Gem className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1453" data-mp="1pswdr2gd2zbs" />
              The Digital Treasury
            </p>
            <h2 className="mt-5 font-serif text-3xl leading-tight text-[#FDFBF7] sm:text-5xl" data-id="element-1454" data-mp="1ktjqwhphps97">
              Treasures of the Sikh Empire.
            </h2>
            <p className="mt-6 max-w-xl text-lg font-light leading-relaxed text-[#FDFBF7]/70" data-id="element-1455" data-mp="1fx2t0vshnzix">
              The Treasury is not a game. It is a digital extension of the
              Lahore Court — a collectible archive where every artifact, figure,
              and place is real history, and where the audience keeps building
              the story between seasons.
            </p>

            <div className="mt-10 grid gap-8 sm:grid-cols-2" data-id="element-1456" data-mp="1ys1bw7o0gqnf">
              {treasuryFeatures.map(feature => {
              const Icon = feature.icon;
              return <div key={feature.title} data-id="element-1457" data-mp="2rayj2zkxlsox">
                    <Icon className="h-5 w-5 text-[#D4AF37]" aria-hidden="true" data-id="element-1458" data-mp="2mjghqaezxq3f" />
                    <h3 className="mt-4 font-serif text-xl text-[#FDFBF7]" data-id="element-1459" data-mp="923zjrub4pzx">
                      {feature.title}
                    </h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-[#FDFBF7]/55" data-id="element-1460" data-mp="e1qcs1a4y68q">
                      {feature.body}
                    </p>
                  </div>;
            })}
            </div>
          </div>

          <figure className="relative" data-id="element-1461" data-mp="s0g8bchb3xuz">
            <div className="border border-[#D4AF37]/30 bg-[#0F0D12] p-2" data-id="element-1462" data-mp="rzenwheehz0l">
              <img src={TREASURY_IMAGE} alt="Founders’ Edition Treasury card depicting Maharaja Ranjit Singh" className="aspect-[4/5] w-full object-cover object-top" data-id="element-1463" data-mp="wxgfjqpw76no" />
            </div>
            <figcaption className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#FDFBF7]/40" data-id="element-1464" data-mp="1aq633zl2kjmw">
              Founders’ Edition · Lahore Court Collection
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Mid-page CTA */}
      <section className="border-b border-[#D4AF37]/12 bg-[#0B0A0E]" data-id="element-1465" data-mp="3bt5h56kx93c6">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-8 px-5 py-14 sm:px-8 lg:flex-row lg:items-center" data-id="element-1466" data-mp="36u2dnqklb56b">
          <p className="max-w-2xl font-serif text-2xl leading-snug text-[#FDFBF7] sm:text-3xl" data-id="element-1467" data-mp="3lji578ry3en2">
            The fastest way to understand this is twenty-five minutes with the
            person building it.
          </p>
          <BookCallButton size="lg" className="shrink-0" data-id="element-1468" data-mp="3qha5aq2qu72c" />
        </div>
      </section>
    </>;
}